import { CashFlowReportRowPath } from "@common/dto/reports/cash-flow-tab-report-response.dto";
import {
    DateOnlyDateRange,
    toDateOnlyDateRange,
    toLocalDateRange,
} from "@design-system/DateRangeFilter/DateRangeFilterNext.lib";
import {
    isTransactionColumn,
    TransactionView,
} from "@common/types/views/transactionViews";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { saveBlobResponse, useFileDownload } from "@lib/fileDownload";
import React, { Fragment, useCallback, useContext, useMemo } from "react";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { useEntities } from "@hooks/useEntities";
import { getTransactionsCsv } from "@lib/transactions";
import { useTransactionViewConfig } from "@components/transactions/views/useTransactionViewConfig";
import { AccountsFilterEntityValueDisplay } from "@components/financialAccount/AccountsFilter/AccountsFilterEntityValueDisplay";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { DateRangeFilterNext } from "@design-system/DateRangeFilter/DateRangeFilterNext";
import { getSortExpression } from "@components/transactions/useSort";
import { ChevronRightSmallIcon, DownloadIcon } from "@icons";
import { HStack } from "@design-system/HStack/HStack";
import { usePinnedDatePresets } from "@hooks/usePinnedDatePresets";
import { ORGANIZATION_USER_CACHE_KEYS } from "@hooks/useOrganizationUserCache";
import { SUGGESTED_PRESETS } from "@helpers/date";
import {
    PaginatedTransactionsActionsContext,
    PaginatedTransactionsDataContext,
} from "../transactions/PaginatedTransactionsTable.context";
import { convertFiltersToDto } from "../transactions/useTransactionsQuery";
import css from "./CashFlowDrillDownHeader.module.scss";

interface CashFlowDrillDownHeaderProps {
    path?: CashFlowReportRowPath[];
    defaultView: TransactionView;
}

export const CashFlowDrillDownHeader: React.FC<
    CashFlowDrillDownHeaderProps
> = ({ path = [], defaultView }) => {
    const activeWorkspace = useActiveWorkspace();
    const { filters, currentView, currentSort } = useContext(
        PaginatedTransactionsDataContext,
    );
    const { setCurrentFilters, setCurrentView } = useContext(
        PaginatedTransactionsActionsContext,
    );

    const entities = useEntities({
        onlyBusiness: true,
        excludeMockEntities: true,
    });

    const selectedEntities = useMemo(
        () => entities.filter((e) => filters.entityIds.includes(e.id)),
        [entities, filters.entityIds],
    );

    const { download: exportTransactions, isDownloading: isExporting } =
        useFileDownload(async () => {
            if (!activeWorkspace?.id) {
                return;
            }

            const response = await getTransactionsCsv({
                workspaceId: activeWorkspace.id,
                filters: convertFiltersToDto(filters, entities),
                columns: currentView.columns
                    .map((column) => column.id)
                    .filter(isTransactionColumn),
                sort: getSortExpression(currentSort),
            });

            saveBlobResponse(response, "transactions.csv");
        });

    const handleDateFilterChange = useCallback<
        (value: DateOnlyDateRange, isAllTime?: boolean) => void
    >(
        (value, isAllTime) => {
            const localRange = toLocalDateRange(value);
            setCurrentFilters({
                ...filters,
                start: isAllTime ? undefined : localRange.start,
                end: isAllTime ? undefined : localRange.end,
            });
        },
        [filters, setCurrentFilters],
    );

    const transactionsViewConfig = useTransactionViewConfig();

    const { pinnedPresets, onTogglePin } = usePinnedDatePresets({
        cacheKey:
            ORGANIZATION_USER_CACHE_KEYS.PINNED_DATE_PRESETS_CASH_FLOW_DRILLDOWN_CACHE_KEY,
        defaults: SUGGESTED_PRESETS.accounting,
    });

    return (
        <div className={css.filters}>
            <HStack gap={12}>
                <div className={css.entityContainer}>
                    <span className={css.label}>
                        <AccountsFilterEntityValueDisplay
                            everythingSelected={false}
                            selectedEntities={selectedEntities}
                            singleEntityName
                            showSelectedIcons
                        />
                    </span>
                </div>
                <Button
                    className={css.breadcrumbButton}
                    variant="primary"
                    size="md"
                >
                    <HStack as="span" gap={8} align="center">
                        {path.map(({ id, label }, index) => (
                            <Fragment key={id}>
                                {index > 0 && (
                                    <ChevronRightSmallIcon
                                        className={css.pathSeparator}
                                        aria-hidden
                                    />
                                )}
                                <span>{String(label)}</span>
                            </Fragment>
                        ))}
                    </HStack>
                </Button>
            </HStack>
            <section className={css.actions}>
                <DateRangeFilterNext
                    dateRange={toDateOnlyDateRange({
                        start: filters.start,
                        end: filters.end,
                    })}
                    onChange={handleDateFilterChange}
                    pinnedPresets={pinnedPresets}
                    onTogglePin={onTogglePin}
                />
                <ButtonWithLoader
                    iconOnly
                    aria-label="Download transactions"
                    onClick={async () => {
                        await exportTransactions();
                    }}
                    loading={isExporting}
                    variant="primary"
                    size="md"
                >
                    <DownloadIcon />
                </ButtonWithLoader>
                <ColumnSettings
                    defaultView={defaultView}
                    viewConfig={transactionsViewConfig}
                    view={currentView}
                    onViewChange={setCurrentView}
                />
            </section>
        </div>
    );
};
